import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Loader2, User, FileText } from 'lucide-react';
import { Input } from '../ui/input';
import { apiCustomers, apiInvoices } from '../../lib/api';

// The search box in the top bar. It used to be an input with nothing behind it:
// it took typing and did nothing with it, on every screen in the app.
//
// Customers and invoices are both small enough to hold in memory — the lists
// are already loaded whole by the pages that show them — so they are fetched
// once on the first search and filtered here. That keeps the typing instant and
// needs no new endpoint.

const LIMIT = 6;

const digits = (value) => String(value || '').replace(/\D/g, '');

export const matchCustomers = (customers, term) => {
  const needle = term.trim().toLowerCase();
  if (!needle) return [];
  const needleDigits = digits(needle);
  return customers.filter((c) => {
    const hay = [c.name, c.company, c.id, c.accountNumber, c.email,
                 c.address, c.fullAddress, c.city, c.state, c.zip]
      .filter(Boolean).join(' ').toLowerCase();
    if (hay.includes(needle)) return true;
    return needleDigits.length >= 3 && digits(c.phone).includes(needleDigits);
  }).slice(0, LIMIT);
};

export const matchInvoices = (invoices, term) => {
  const needle = term.trim().toLowerCase();
  if (!needle) return [];
  return invoices.filter((i) => {
    const hay = [i.number, i.invoiceNumber, i.id, i.customerName, i.customer,
                 i.customerAccountNumber, i.customerAccount, i.status]
      .filter(Boolean).join(' ').toLowerCase();
    return hay.includes(needle);
  }).slice(0, LIMIT);
};

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [book, setBook] = useState(null);     // null until the first search
  const box = useRef(null);

  const load = useCallback(() => {
    if (book || loading) return;
    setLoading(true);
    Promise.all([
      apiCustomers.list().catch(() => []),
      apiInvoices.list().catch(() => []),
    ])
      .then(([customers, invoices]) => setBook({
        customers: customers || [], invoices: invoices || [],
      }))
      .finally(() => setLoading(false));
  }, [book, loading]);

  // A result leads somewhere else in the app, so the list has to go when it does.
  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const customers = useMemo(
    () => matchCustomers(book?.customers || [], q), [book, q]);
  const invoices = useMemo(
    () => matchInvoices(book?.invoices || [], q), [book, q]);

  // Picking a result empties the box: the next search starts clean rather than
  // appending to what was looked up last time.
  const goCustomer = (c) => { setQ(''); setOpen(false); navigate(`/app/customers/${c.id}`); };
  const goInvoice = (i) => {
    const term = i.number || i.invoiceNumber || i.id || '';
    setQ('');
    setOpen(false);
    navigate(`/app/accounting/invoices?q=${encodeURIComponent(term)}`);
  };

  const onType = (e) => {
    setQ(e.target.value);
    setOpen(true);
    load();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setQ(''); setOpen(false); return; }
    if (e.key !== 'Enter') return;
    if (customers.length) goCustomer(customers[0]);
    else if (invoices.length) goInvoice(invoices[0]);
  };

  const term = q.trim();
  const nothing = term && !loading && book && !customers.length && !invoices.length;

  return (
    <div className="relative" ref={box}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
      <Input
        value={q}
        onChange={onType}
        onFocus={() => { if (term) setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder="Search customers, invoices…"
        className="pl-9 h-10 bg-slate-50 border-slate-200"
        data-testid="global-search-input"
      />
      {open && term && (
        <div
          className="absolute left-0 right-0 top-12 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50 max-h-[70vh] overflow-y-auto"
          data-testid="global-search-results"
        >
          {loading && (
            <div className="px-3 py-3 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin"/> Searching…
            </div>
          )}
          {!!customers.length && (
            <div>
              <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Customers</div>
              {customers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => goCustomer(c)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2"
                  data-testid="global-search-customer"
                >
                  <User className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0"/>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900 truncate">{c.name || c.company || c.id}</span>
                    <span className="block text-xs text-slate-500 truncate">
                      {[c.accountNumber && `#${c.accountNumber}`, c.address || c.fullAddress, c.phone]
                        .filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {!!invoices.length && (
            <div className="border-t border-slate-100">
              <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Invoices</div>
              {invoices.map((i) => (
                <button
                  key={i.id || i.number}
                  onClick={() => goInvoice(i)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2"
                  data-testid="global-search-invoice"
                >
                  <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0"/>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900 truncate">
                      {i.number || i.invoiceNumber || i.id} · {money(i.total)}
                    </span>
                    <span className="block text-xs text-slate-500 truncate">
                      {[i.customerName || i.customer, i.date, i.status].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {nothing && (
            <div className="px-3 py-3 text-sm text-slate-500">
              Nothing matches “{term}”.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
